import { useEffect, useCallback, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { get, isArray } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { insertIf } from '@/helpers/general-helpers'
import { useLoggedInUser } from '@/hooks'
import { TableColumnFilterDefinition } from '@/types/Table'
import { DocumentNode } from 'graphql'

interface UseSelectableFiltersProps {
  columnsFilters?: TableColumnFilterDefinition
  selectedFilters?: any[]
}

const useSelectableFilters = ({
  // Available filters to select. Can either be an array of options, or an object containing a graphql query
  columnsFilters = { options: [] },
  // Filters selected in the column
  selectedFilters = [],
}: UseSelectableFiltersProps) => {
  const { t } = useTranslation()
  const { companyId } = useLoggedInUser()
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [shouldLoadFilters, setShouldLoadFilters] = useState(false)
  const [selectableFilters, setSelectableFilters] = useState(
    isArray(columnsFilters.options) ? columnsFilters.options : [],
  )

  useEffect(() => {
    if (columnsFilters?.query && shouldLoadFilters) {
      const updateFilters = async () => {
        setLoading(true)

        try {
          const { data } = await client.query({
            query: columnsFilters.query as DocumentNode,
            variables: {
              ...(columnsFilters?.variables ?? {}),
              companyId,
            },
          })

          const { valueField, textField, render } = columnsFilters

          let newSelectableFilters = []

          if (data) {
            newSelectableFilters = data[Object.keys(data)[0]].edges.map(
              ({ node }: any) => ({
                value: get(node, valueField as string) ?? null,
                // use lodash get instead of optional chain operator so we can support dot syntax like 'user.fullName'
                text: get(node, textField as string) ?? null,
                node: node,
                render: render || null,
              }),
            )
          }
          setSelectableFilters(newSelectableFilters)
        } catch (e) {
        } finally {
          setLoading(false)
        }
      }

      updateFilters()
    }
  }, [columnsFilters, shouldLoadFilters, companyId, t, client])

  const loadFilters = useCallback(() => setShouldLoadFilters(true), [])

  // If the column already has filters selected,
  // we have to immediately run the query to show them
  useEffect(() => {
    if (columnsFilters?.query && selectedFilters?.length) {
      loadFilters()
    }
  }, [columnsFilters?.query, selectedFilters?.length, loadFilters])

  return {
    selectableFilters: [
      ...selectableFilters,
      ...insertIf(!!columnsFilters.addNullFilter, {
        value: null,
        text: t('No value'),
      }),
    ],
    loadFilters,
    loading,
  }
}

export default useSelectableFilters
